import type { IApplication } from "@/modules/application/common/types";
import { DATE_FORMAT } from "@/shared/common/constants";
import {
  CloudflareAsset,
  OperationCommand,
  OperationStatus,
  OperationStatusColor
} from "@/shared/common/enums";
import type { IActionsMenuAction } from "@/shared/common/types";
import { ActionsMenu, CloudFlareImage } from "@/shared/components";
import { formatDate } from "@/shared/helpers";
import type { ICellProps } from "@ab.uitools/ui-library/components/AdvancedTable/types";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Chips } from "@ab.uitools/ui-library/components/Chips";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Tooltip } from "@ab.uitools/ui-library/components/Tooltip";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";

interface Props {
  t: (s: string) => string;
  onSign: (id: IApplication) => void;
  isDesktop: boolean;
  rowId?: string | null;
  openMenu: (application: IApplication, index: string) => void;
  isOpenMenu: boolean;
  closeMenu: () => void;
  actions: IActionsMenuAction[];
}

export const useApplicationColumns = ({
  t,
  rowId,
  onSign,
  openMenu,
  closeMenu,
  actions,
  isDesktop,
  isOpenMenu,
}: Props) => [
  {
    id: "creationDate",
    header: t("applicationHistoryTable.creationDate"),
    accessorKey: "creationDate",
    enableHiding: false,
    cell: ({ row }: ICellProps<IApplication>) => {
      const date = row.original?.creationDate;
      return (
        <Text>
          {formatDate(date, DATE_FORMAT.DD_MM_YYYY)}
        </Text>
      );
    },
  },
  {
    id: "application_number",
    header: t("applicationHistoryTable.applicationNumber"),
    accessorKey: "applicationNumber",
    enableHiding: false,
  },
  {
    id: "application_type",
    header: t("applicationHistoryTable.applicationType"),
    accessorKey: "applicationType",
    enableHiding: false,
    cell: ({ row }: ICellProps<IApplication>) => {
      const applicationType = row.original?.applicationType;
      return (
        <Text>{t(`applicationTypes.${applicationType}`)}</Text>
      );
    },
  },
  {
    id: "purpose",
    header: t("applicationHistoryTable.purpose"),
    accessorKey: "purpose",
  },
  {
    id: "status",
    header: t("applicationHistoryTable.status"),
    accessorKey: "state.transactionStatus",
    enableHiding: false,
    cell: ({ row }: ICellProps<IApplication>) => {
      const status = row.original?.state?.applicationStatus;
      return (
        <div>
          <Chips
            type="accent"
            color={OperationStatusColor[status]}
            text={t(`documentStatus.${OperationStatus[status]}`)}
          />
        </div>
      );
    },
  },
  {
    id: "actions",
    header: t("applicationHistoryTable.actions"),
    accessorKey: "actions",
    maxSize: 100,
    cell: ({ row }: ICellProps<IApplication>) => {
      const isSignable = row.original.state?.applicationCommand?.includes(
        OperationCommand.Sign
      );

      const filteredActions = actions.filter((action) => {
        return row.original.state?.applicationCommand?.includes(action.value as string);
      }).map((action) => ({
        ...action, handler: () => action.handler?.({
          operationId: row.original?.id,
          operationType: row.original?.applicationType,
        })
      }));

      return (
        <div className="flexbox align-items--center gap-8">
          <Tooltip
            position={Positions.MIDDLE_LEFT}
            id={`${row.id}_sign-icon-button`}
            text={t("general.sign")}
          />
          {isSignable && (
            <Button
              onClick={() => onSign(row.original)}
              type="primary"
              size={isDesktop ? "large" : "medium"}
              id={`${row.id}_sign-icon-button`}
              data-testid="application-sign"
              data-ga-id="transaction-sign"
              data-ga-action="Transfers history table sign button"
              iconProps={{
                Component: (props: ISVGIconProps) => (
                  <CloudFlareImage {...props} path={CloudflareAsset.SignLight}/>
                ),
              }}
            />
          )}
          {!!filteredActions.length && (
            <ActionsMenu
              isOpen={row.original.id === rowId && isOpenMenu}
              actions={filteredActions}
              parentClass="advanced-table__inner"
              openMenu={() => openMenu(row.original, row.id)}
              isInTable
              position={Positions.BOTTOM_RIGHT}
              closeMenu={closeMenu}
            />
          )}
        </div>
      );
    },
  },
];
